import { backgroundColor } from "@/utils/theme";
import {
	Check,
	Close,
	DeleteOutline,
	EditOutlined,
	ErrorOutline,
} from "@mui/icons-material";
import {
	Box,
	CircularProgress,
	IconButton,
	IconButtonProps,
	SxProps,
	TableCell,
	TableRow,
	Theme,
	Tooltip,
} from "@mui/material";
import { ReactNode } from "react";
import {
	WATER_READ_TYPE_LABEL,
	WaterBillTarget,
	WaterReadType,
} from "../../models/WaterBilling";
import {
	formatBillingDate,
	isDateBilled,
	toBillingDate,
	WaterBillGridRow,
	WaterBillPeriod,
} from "../../utils/billRows";
import { DateCell, NumberCell, ReadTypeCell, TextCell } from "./Cell";
import { BillEditing } from "./hooks/useBillEditing";

const asFormatter = (options: Intl.NumberFormatOptions) => {
	const intl = new Intl.NumberFormat(undefined, options);
	return (value: number | null) => (value === null ? "" : intl.format(value));
};

export const formatGallons = asFormatter({ maximumFractionDigits: 0 });

export const formatMoney = asFormatter({
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

export const formatPercent = asFormatter({ maximumFractionDigits: 2 });

export const formatReadType = (value: WaterReadType | null) =>
	value === null ? "" : WATER_READ_TYPE_LABEL[value];

const CENTER_SX = { textAlign: "center" } as const;
const FIGURE_SX = { textAlign: "right" } as const;
const DERIVED_FIGURE_SX = {
	color: "#7a7a7a",
	backgroundColor: "grey.100",
	...FIGURE_SX,
} as const;
const DERIVED_CENTER_SX = {
	color: "#7a7a7a",
	backgroundColor: "grey.100",
	...CENTER_SX,
} as const;

const ACTIONS_SX = {
	"& > *": { display: "inline-flex", verticalAlign: "middle" },
	textAlign: "center",
	whiteSpace: "nowrap",
} as const;

type PatchRow = BillEditing["patchRow"];
const fieldPatcher =
	(row: WaterBillGridRow, patchRow: PatchRow) =>
	<K extends keyof WaterBillGridRow>(key: K) =>
	(value: WaterBillGridRow[K]) =>
		patchRow(row.id, { [key]: value } as Partial<WaterBillGridRow>);

type EditableCellProps = {
	isEditing: boolean;
	editor: ReactNode;
	children?: ReactNode;
	rowSpan?: number;
	sx?: SxProps<Theme>;
};
const EditableCell = ({
	isEditing,
	editor,
	children,
	...cell
}: EditableCellProps) => (
	<TableCell {...cell}>{isEditing ? editor : children}</TableCell>
);

type TrackedCellProps = {
	value: number | null;
	isStale: boolean;
	isLoading: boolean;
	rowSpan?: number;
};

const TrackedCell = ({
	value,
	isStale,
	isLoading,
	rowSpan,
}: TrackedCellProps) => {
	const marker = isLoading ? (
		<Tooltip title="Recalculating the tracked gallons for the new service period…">
			<CircularProgress size={10} thickness={6} sx={{ color: "info.main" }} />
		</Tooltip>
	) : isStale ? (
		<Tooltip title="The service period changed: the tracked gallons still belong to the saved period and will be recalculated once the changes are saved.">
			<ErrorOutline sx={{ color: "warning.main", fontSize: 12 }} />
		</Tooltip>
	) : null;

	return (
		<TableCell rowSpan={rowSpan} sx={DERIVED_FIGURE_SX}>
			{marker === null ? (
				formatGallons(value)
			) : (
				<Box
					component="span"
					sx={{ alignItems: "center", display: "inline-flex", gap: 0.25 }}
				>
					{formatGallons(value)}
					{marker}
				</Box>
			)}
		</TableCell>
	);
};

type ActionButtonProps = {
	title: string;
	color?: IconButtonProps["color"];
	disabled?: boolean;
	onClick: () => void;
	children: ReactNode;
};

const ActionButton = ({
	title,
	color,
	disabled = false,
	onClick,
	children,
}: ActionButtonProps) => (
	<Tooltip title={title}>
		<span>
			<IconButton
				size="small"
				color={color}
				sx={{ p: 0.25 }}
				disabled={disabled}
				onClick={onClick}
			>
				{children}
			</IconButton>
		</span>
	</Tooltip>
);

type TrackedState = {
	isStale: boolean;
	isLoading: boolean;
};

type CellsProps = {
	row: WaterBillGridRow;
	isEditing: boolean;
	tracked: TrackedState;
	patchRow: PatchRow;
};

const ReadingCells = ({
	row,
	isEditing,
	tracked,
	patchRow,
	periods,
}: CellsProps & { periods: WaterBillPeriod[] }) => {
	const isMeterLine = row.meterId !== null;
	const patch = fieldPatcher(row, patchRow);

	const periodDateCell = (key: "servicePeriodStart" | "servicePeriodEnd") => (
		<EditableCell
			rowSpan={row.billRowCount}
			sx={CENTER_SX}
			isEditing={isEditing}
			editor={
				<DateCell
					value={row[key]}
					shouldDisableDate={(day) =>
						isDateBilled(periods, toBillingDate(day), row.billId)
					}
					onChange={patch(key)}
				/>
			}
		>
			{formatBillingDate(row[key])}
		</EditableCell>
	);

	const readTypeCell = (key: "startReadType" | "endReadType") => (
		<EditableCell
			sx={CENTER_SX}
			isEditing={isEditing && isMeterLine}
			editor={<ReadTypeCell value={row[key]} onChange={patch(key)} />}
		>
			{isMeterLine ? formatReadType(row[key]) : null}
		</EditableCell>
	);

	return (
		<>
			<TableCell sx={DERIVED_CENTER_SX}>{row.meterNumber}</TableCell>
			<TableCell sx={DERIVED_CENTER_SX}>{row.meterSize ?? ""}</TableCell>
			{row.isFirstOfBill && periodDateCell("servicePeriodStart")}
			{readTypeCell("startReadType")}
			{row.isFirstOfBill && periodDateCell("servicePeriodEnd")}
			{readTypeCell("endReadType")}
			<EditableCell
				sx={FIGURE_SX}
				isEditing={isEditing && isMeterLine}
				editor={
					<NumberCell value={row.gallons} min={0} onChange={patch("gallons")} />
				}
			>
				{isMeterLine ? formatGallons(row.gallons) : null}
			</EditableCell>
			<TrackedCell
				value={isMeterLine ? row.trackedGallons : null}
				isStale={isMeterLine && tracked.isStale}
				isLoading={isMeterLine && tracked.isLoading}
			/>
		</>
	);
};

const BillingCells = ({ row, isEditing, tracked, patchRow }: CellsProps) => {
	const patch = fieldPatcher(row, patchRow);
	const span = row.billRowCount;

	return (
		<>
			<TableCell rowSpan={span} sx={DERIVED_FIGURE_SX}>
				{formatGallons(row.totalGallons)}
			</TableCell>
			<TrackedCell
				value={row.trackedTotalGallons}
				isStale={tracked.isStale}
				isLoading={tracked.isLoading}
				rowSpan={span}
			/>
			<EditableCell
				rowSpan={span}
				sx={FIGURE_SX}
				isEditing={isEditing}
				editor={
					<NumberCell
						value={row.balanceForward}
						onChange={patch("balanceForward")}
					/>
				}
			>
				{formatMoney(row.balanceForward)}
			</EditableCell>
			<EditableCell
				rowSpan={span}
				sx={FIGURE_SX}
				isEditing={isEditing}
				editor={
					<NumberCell value={row.currentBill} onChange={patch("currentBill")} />
				}
			>
				{formatMoney(row.currentBill)}
			</EditableCell>
			<EditableCell
				rowSpan={span}
				sx={CENTER_SX}
				isEditing={isEditing}
				editor={
					<TextCell value={row.checkNumber} onChange={patch("checkNumber")} />
				}
			>
				{row.checkNumber ?? ""}
			</EditableCell>
			<EditableCell
				rowSpan={span}
				sx={CENTER_SX}
				isEditing={isEditing}
				editor={<DateCell value={row.payDate} onChange={patch("payDate")} />}
			>
				{formatBillingDate(row.payDate)}
			</EditableCell>
		</>
	);
};

const AllocationCells = ({
	row,
	isEditing,
	targets,
	balancingCode,
	patchPercent,
}: Omit<CellsProps, "tracked" | "patchRow"> & {
	targets: WaterBillTarget[];
	balancingCode: string | null;
	patchPercent: BillEditing["patchPercent"];
}) => (
	<>
		{targets.map(({ TargetId, TargetCode }) => (
			<EditableCell
				key={`pct-${TargetId}`}
				rowSpan={row.billRowCount}
				sx={FIGURE_SX}
				isEditing={isEditing}
				editor={
					<NumberCell
						value={row.percentByTarget[TargetCode] ?? null}
						min={0}
						max={100}
						muted={balancingCode === TargetCode}
						onChange={(value) => patchPercent(row, TargetCode, value)}
					/>
				}
			>
				{formatPercent(row.percentByTarget[TargetCode] ?? null)}
			</EditableCell>
		))}
		{targets.map(({ TargetId, TargetCode }) => (
			<TableCell
				key={`brk-${TargetId}`}
				rowSpan={row.billRowCount}
				sx={DERIVED_FIGURE_SX}
			>
				{formatGallons(row.breakdownByTarget[TargetCode] ?? null)}
			</TableCell>
		))}
		{targets.map(({ TargetId, TargetCode }) => (
			<TableCell
				key={`cost-${TargetId}`}
				rowSpan={row.billRowCount}
				sx={DERIVED_FIGURE_SX}
			>
				{formatMoney(row.costByTarget[TargetCode] ?? null)}
			</TableCell>
		))}
	</>
);

type BillRowProps = {
	row: WaterBillGridRow;
	targets: WaterBillTarget[];
	periods: WaterBillPeriod[];
	editing: BillEditing;
	isTrackedLoading: boolean;
	isTrackedFresh: boolean;
	onBillDelete: (billId: number) => void;
};

export const BillRow = ({
	row,
	targets,
	periods,
	editing,
	isTrackedLoading,
	isTrackedFresh,
	onBillDelete,
}: BillRowProps) => {
	const isEditing = editing.isEditing(row.billId);
	const isLocked = editing.isLocked(row.billId);
	const isPeriodStale = editing.isPeriodDirty(row.billId);
	const error = editing.getError(row);

	const tracked: TrackedState = {
		isLoading: isPeriodStale && isTrackedLoading,
		isStale: isPeriodStale && !isTrackedLoading && !isTrackedFresh,
	};

	const isSessionOpen = isEditing || isLocked;

	return (
		<TableRow
			sx={{
				...(isSessionOpen
					? null
					: { "&:hover td": { backgroundColor: backgroundColor } }),
				opacity: isLocked ? 0.5 : 1,
			}}
		>
			<ReadingCells
				row={row}
				isEditing={isEditing}
				tracked={tracked}
				patchRow={editing.patchRow}
				periods={periods}
			/>
			{row.isFirstOfBill && (
				<>
					<BillingCells
						row={row}
						isEditing={isEditing}
						tracked={tracked}
						patchRow={editing.patchRow}
					/>
					<AllocationCells
						row={row}
						isEditing={isEditing}
						targets={targets}
						balancingCode={editing.balancingCode}
						patchPercent={editing.patchPercent}
					/>
					<TableCell rowSpan={row.billRowCount} sx={ACTIONS_SX}>
						{isEditing ? (
							<>
								<ActionButton
									title="Discard changes"
									onClick={editing.cancelEdit}
								>
									<Close fontSize="small" />
								</ActionButton>
								<ActionButton
									title={error ?? "Save changes"}
									color="primary"
									disabled={error !== null}
									onClick={() => editing.confirmEdit(row.billId)}
								>
									<Check fontSize="small" />
								</ActionButton>
							</>
						) : (
							<>
								<ActionButton
									title="Edit bill"
									disabled={isLocked}
									onClick={() => editing.startEdit(row.billId)}
								>
									<EditOutlined fontSize="small" />
								</ActionButton>
								<ActionButton
									title="Delete bill"
									disabled={isLocked}
									onClick={() => onBillDelete(row.billId)}
								>
									<DeleteOutline fontSize="small" />
								</ActionButton>
							</>
						)}
					</TableCell>
				</>
			)}
		</TableRow>
	);
};
